import { find } from 'lodash';
import promotions from '../config/promotions-config.json';

let configManager: Promotion | null = null;
let currentPromotion = '';

class Promotion {
  data: any;

  constructor(promotionId) {
    this.data = find(
      (promotions as any).items,
      ({ fields }) => fields?.promotionId === promotionId
    );
  }

  get allFields() {
    return this.data?.fields || {};
  }
}

function initPromotionConfig(promotionId: string) {
  if (configManager && currentPromotion === promotionId) {
    return configManager?.allFields;
  }

  configManager = new Promotion(promotionId);
  currentPromotion = promotionId;

  return configManager?.allFields;
}

export function getPromoImages(promotionId: string) {
  const { headerLogo } = initPromotionConfig(promotionId);
  return {
    headerLogoImage: headerLogo?.fields?.file?.url
  };
}

export function getConfirmCodePromoConfig(promotionId: string) {
  const { confirmCodeCtaHeader, confirmCodeCtaSubheader } =
    initPromotionConfig(promotionId);
  return {
    header: confirmCodeCtaHeader,
    subheader: confirmCodeCtaSubheader
  };
}

export function getSetPlanPromoConfig(promotionId: string) {
  const {
    setPlanHeader,
    setPlanSubheader,
    setPlanInvestMessage,
    setPlanMaxMessage,
    setPlanInvestButton,
    setPlanMaxButton
  } = initPromotionConfig(promotionId);

  return {
    header: setPlanHeader,
    subheader: setPlanSubheader,
    pricingMessageInvest: setPlanInvestMessage,
    pricingMessageMax: setPlanMaxMessage,
    investButton: setPlanInvestButton,
    maxButton: setPlanMaxButton
  };
}

export function getFundingAccountPromoConfig(
  promotionId: string,
  planName: string,
  currentFee: number
) {
  const {
    fundingAccountCtaHeaderInvest,
    fundingAccountCtaSubheaderInvest,
    fundingAccountCtaHeaderMax,
    fundingAccountCtaSubheaderMax,
    fundingAccountCtaHeaderStandard,
    fundingAccountCtaSubheaderStandard
  } = initPromotionConfig(promotionId);

  let header;
  let subheader;

  switch (planName) {
    case 'invest':
      header = fundingAccountCtaHeaderInvest;
      subheader = fundingAccountCtaSubheaderInvest;
      break;

    case 'max':
      header = fundingAccountCtaHeaderMax;
      subheader = fundingAccountCtaSubheaderMax;
      break;

    case 'standard':
    default:
      header = fundingAccountCtaHeaderStandard;
      subheader = fundingAccountCtaSubheaderStandard;
  }

  if (subheader && subheader.includes('{monthly_fee}')) {
    subheader = subheader.replace('{monthly_fee}', currentFee.toString());
  }

  return { header, subheader };
}

export function getCompletePromoConfig(
  promotionId: string,
  childrenNames: string[]
) {
  const { completePageImage, completePageTitle } =
    initPromotionConfig(promotionId);
  const image = completePageImage?.fields.file?.url;
  let header = completePageTitle;

  // Replace string {children_names} in completePageTitle with children name(s)
  if (header && childrenNames && childrenNames.length) {
    if (header.includes('{children_names}')) {
      let childrenText = childrenNames[0];

      if (childrenNames.length === 2) {
        childrenText = `${childrenNames[0]} and ${childrenNames[1]}`;
      }

      if (childrenNames.length > 2) {
        const last = childrenNames.pop();
        childrenText = `${childrenNames.join(', ')}, and ${last}`;
      }

      header = header.replace('{children_names}', childrenText);
    }
    // Replace string {card} with 'card' or 'cards' depending on number of children
    if (header.includes('{card}')) {
      const cardText = childrenNames.length > 1 ? 'cards' : 'card';
      header = header.replace('{card}', cardText);
    }
  }

  return { header, image };
}

export function getCompleteAppLinkPromoConfig(
  promotionId: string,
  appStore: string
) {
  const {
    completePageAppleLink,
    completePageAmazonLink,
    completePageGoogleLink
  } = initPromotionConfig(promotionId);

  switch (appStore) {
    case 'appleAppStore':
      return completePageAppleLink;
    case 'googlePlayStore':
      return completePageGoogleLink;
    case 'amazonAppStore':
      return completePageAmazonLink;
    default:
      return null;
  }
}

export function getCompletePricingPromoConfig(
  promotionId: string,
  planName: string,
  monthlyFee: number,
  hasMultipleChildren: boolean | undefined
) {
  const {
    completePagePricingMessageInvest,
    completePagePricingMessageMax,
    completePagePricingMessageStandard
  } = initPromotionConfig(promotionId);

  let promoPricingMessage;

  switch (planName) {
    case 'invest':
      promoPricingMessage = completePagePricingMessageInvest;
      break;

    case 'max':
      promoPricingMessage = completePagePricingMessageMax;
      break;

    case 'standard':
    default:
      promoPricingMessage = completePagePricingMessageStandard;
  }

  if (promoPricingMessage && promoPricingMessage.includes('{monthly_fee}')) {
    promoPricingMessage = promoPricingMessage.replace(
      '{monthly_fee}',
      monthlyFee.toString()
    );
  }

  if (promoPricingMessage && promoPricingMessage.includes('{card}')) {
    const cardText = hasMultipleChildren ? 'cards' : 'card';
    promoPricingMessage = promoPricingMessage.replace('{card}', cardText);
  }

  return promoPricingMessage;
}
